import useScheduleQueryPatientId from "@/hooks/useScheduleQueryPatientId";
import {
  useRegisterForGroupClassMutation,
  useUnregisterForGroupClassMutation,
} from "@/store/api/schedule/groupClasses";

type OnCompletionCallback = ({
  isRegistered,
  wasRegistered,
}: {
  isRegistered: boolean;
  wasRegistered: boolean;
}) => void;

type GroupClassRegistrationWithCallback = (
  onCompletion?: OnCompletionCallback,
) => void;

type UseGroupClassRegistrationState = {
  isLoading: boolean;
  registerForGroupClass: GroupClassRegistrationWithCallback;
  unregisterForGroupClass: GroupClassRegistrationWithCallback;
};

const useGroupClassRegistration = ({
  groupClassExternalId,
  patientExternalId: patientIdArg,
}: {
  groupClassExternalId: string;
  onCompletion?: OnCompletionCallback;
  patientExternalId: string;
}): UseGroupClassRegistrationState => {
  // require all callers of this hook to provide an explicit patient external id
  // and this is the one spot where, for the app user, that gets mapped to undefined
  // to prevent bugs across callers having to enforce that convention
  const patientExternalId = useScheduleQueryPatientId(patientIdArg);

  const [registerForGroupClassTrigger, { isLoading: isRegisterLoading }] =
    useRegisterForGroupClassMutation();
  const [unregisterForGroupClassTrigger, { isLoading: isUnregisterLoading }] =
    useUnregisterForGroupClassMutation();
  const isLoading = isRegisterLoading || isUnregisterLoading;

  const registerForGroupClass: GroupClassRegistrationWithCallback = (
    onCompletion,
  ) => {
    // if registering, then isRegistered is currently false
    const wasRegistered = false;
    registerForGroupClassTrigger({
      groupClassExternalId,
      patientExternalId,
    })
      .unwrap()
      .then(() => onCompletion?.({ isRegistered: true, wasRegistered }))
      .catch(() => onCompletion?.({ isRegistered: false, wasRegistered }));
  };

  const unregisterForGroupClass: GroupClassRegistrationWithCallback = (
    onCompletion,
  ) => {
    // if unregistering, then isRegistered is currently true
    const wasRegistered = true;
    unregisterForGroupClassTrigger({
      groupClassExternalId,
      patientExternalId,
    })
      .unwrap()
      .then(() => onCompletion?.({ isRegistered: false, wasRegistered }))
      .catch(() => onCompletion?.({ isRegistered: true, wasRegistered }));
  };

  return {
    isLoading,
    registerForGroupClass,
    unregisterForGroupClass,
  };
};

export type { OnCompletionCallback };
export { useGroupClassRegistration };
